import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { getToken, onMessage, isSupported } from 'firebase/messaging'
import { auth, messaging } from './firebase'
import { saveUserToken } from './services/UserProfileService'

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID

// 🔔 Reusable notification helper
function showNotification(title: string, options: NotificationOptions) {
  if (Notification.permission === 'granted') {
    try {
      new Notification(title, options)
    } catch (err) {
      console.warn('⚠️ Failed to show Notification, falling back to SW:', err)
      if (
        'serviceWorker' in navigator &&
        (window as any).serviceWorkerRegistration
      ) {
        ;(window as any).serviceWorkerRegistration.showNotification(
          title,
          options
        )
      }
    }
  } else {
    console.warn('🚫 Notification permission not granted')
  }
}

// ✅ Handle foreground messages (tab is open/active)
onMessage(messaging, (payload) => {
  console.log('📩 Foreground message received:', payload)

  const { title, body } = payload.notification || {}
  showNotification(title || 'New Message', {
    body: body || '',
    icon: '/andyanime.png',
    data: payload.data || {},
    requireInteraction: true, // keeps it visible until user dismisses
    tag: payload.messageId, // prevents duplicates
  })
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <GoogleOAuthProvider clientId={CLIENT_ID}>
      <App />
    </GoogleOAuthProvider>
  </StrictMode>
)

// ✅ Initialize Firebase Messaging (register SW, don’t ask permission yet)
const initializeMessaging = async () => {
  try {
    const isMessagingSupported = await isSupported()
    if (!isMessagingSupported) {
      console.warn('🚫 Firebase Messaging is not supported in this browser')
      return
    }

    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
        { scope: '/' }
      )
      console.log(
        '✅ Service Worker registered with scope:',
        registration.scope
      )

      // Save registration globally for fallback notifications
      ;(window as any).serviceWorkerRegistration = registration
    }
  } catch (error) {
    console.error('❌ Messaging initialization failed:', error)
  }
}

// ✅ Request permission + FCM token
export const requestForToken = async () => {
  try {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      console.warn('🚫 Notification permission denied:', permission)
      return null
    }

    const registration = await navigator.serviceWorker.ready
    const currentToken = await getToken(messaging, {
      vapidKey: import.meta.env.VITE_VAPID_API_KEY,
      serviceWorkerRegistration: registration,
    })

    if (currentToken) {
      console.log('✅ Got FCM token:', currentToken)

      const user = auth.currentUser
      if (user) {
        await saveUserToken(user.uid, currentToken)
        console.log('✅ Token saved to Firestore for user:', user.uid)
      }
      return currentToken
    } else {
      console.warn('⚠️ No registration token available.')
      return null
    }
  } catch (err) {
    console.error('❌ Error retrieving FCM token:', err)
    return null
  }
}

// 👇 For manual testing in browser console
;(window as any).requestForToken = requestForToken

// ✅ Extra logging for service worker events
navigator.serviceWorker.addEventListener('message', (event) => {
  console.log('📩 Service Worker message:', event.data)
})
navigator.serviceWorker.addEventListener('error', (event) => {
  console.error('❌ Service Worker error:', event)
})

// Start service worker registration
initializeMessaging()
